import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Formik, Form, useFormikContext, FormikHelpers, FastField, Field } from "formik";
import * as Yup from "yup";
import HorizontalSeparator from "../../components/common/HorizontalSeparator";
import { Box, Tabs, Tab } from "@mui/material";
import { useGlobalState } from "../../context/useGlobalState";
import { createSingleTrackStory } from "../../api/singleTrack";
import { getProjects, getProject } from "../../api";
import Button from "../../components/ui/Button";
import { FileInput } from "../../components/common/FileInput";
import { uploadFileToS3Complete } from "../../api/s3";
import ImageList from "../../components/images/ImageList";
import { TextField, NumberField } from "../../components/common/FormFields";
import FormGroup from "../../components/common/FormGroup";
import ImageSpecsBadges from "../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../hooks/useImageValidation";
import { Tooltip } from "@mui/material";
import UnifiedSingleTrackCreationModal from "../../components/modals/UnifiedSingleTrackCreationModal";
import { CompanySingleTrackFolder } from "../../types/requestsOutputs";
import AudioPlayerRewind from "../../components/common/AudioPlayer";
import Dropdown from "../../components/common/Dropdown";
import RadioButton from "../../components/common/RadioButton";
import activeRadio from "../../assets/images/radio_active.png";
import inactiveRadio from "../../assets/images/radio_inactive.png";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../utils/image";
import stepStyles from "../track/create/createStep.module.css";
import audioForm from "../../components/common/AudioPlayerForm.module.css";

const audioAccept = {
    "audio/mpeg": [".mp3"],
    "audio/wav": [".wav"],
};

interface LocalizedContent {
    name: string;
    description?: string;
    audioUrl: string;
    useProject: boolean;
    selectedProject: string;
    selectedAudioOption: string;
}

interface UnifiedFormValues {
    localizedContent: {
        [key: string]: LocalizedContent;
    };
    // Information block fields (all optional)
    address?: string;
    coordinates?: string;
    phone?: string;
    linkUrl?: string;
    downloadLink?: string;
    // Common fields
    pictures: string[];
    type: "story" | "event";
    position?: number;
}

interface UnifiedSingleTrackFormProps {
    setActiveTab: (
        tab: "newSingleTrack" | "activeSingleTracks" | "newSingleTrackStory" | "activeSingleTrackStories",
    ) => void;
    folderId?: string | null;
    categoryId?: string | null;
    hubId?: string | null;
    hub?: CompanySingleTrackFolder | null;
    onSuccess?: () => void;
}

const UnifiedSingleTrackForm: React.FC<UnifiedSingleTrackFormProps> = ({
    setActiveTab,
    folderId,
    categoryId,
    hubId,
    hub,
    onSuccess,
}) => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();
    const navigate = useNavigate();

    const [projects, setProjects] = useState<any[]>([]);
    const [projectsData, setProjectsData] = useState<{ [key: string]: any }>({});
    const [loadingProjects, setLoadingProjects] = useState(true);

    // Check if all required data is loaded
    const isDataLoading = loadingProjects;

    // Fetch projects for audio selection
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    setLoadingProjects(false);
                    return;
                }

                const response = await getProjects({ token });
                if (response) {
                    setProjects(response);
                }
            } catch (error) {
                console.error("Error fetching projects:", error);
            } finally {
                setLoadingProjects(false);
            }
        };

        fetchProjects();
    }, []);

    // Get languages from hub (mandatory, cannot be changed)
    const getHubLanguages = () => {
        if (hub?.availableLanguagesTab && hub.availableLanguagesTab.length > 0) {
            return hub.availableLanguagesTab.map((langCode: string) => {
                const lang = data.languagesAI?.find((l: any) => l.code === langCode);
                return {
                    code: langCode,
                    name: lang ? lang.label : langCode,
                };
            });
        }
        // Fallback to default languages if hub doesn't have them
        return [
            { code: "EN", name: "English" },
            { code: "FR", name: "Français" },
        ];
    };

    const LANGUAGES = getHubLanguages();

    const [status, setStatus] = useState<"waiting" | "success" | "error">("waiting");
    const [modalOpen, setModalOpen] = useState(false);
    const [activeLanguageTab, setActiveLanguageTab] = useState(0);

    // Handle language tab change
    const handleLanguageTabChange = (newTabIndex: number) => {
        setActiveLanguageTab(newTabIndex);
    };

    // Validation schema - simplified
    const validationSchema = Yup.object()
        .shape({
            type: Yup.string().oneOf(["story", "event"]).required(),
            pictures: Yup.array().of(Yup.string()),
            position: Yup.number(),

            // Information block fields (all optional)
            address: Yup.string(),
            coordinates: Yup.string()
                .nullable()
                .test("is-valid-coordinates", t("coordinatesMustBeValid"), function (value) {
                    if (!value || value.trim() === "") return true;
                    const parts = value.split(",").map((val) => val.trim());
                    if (parts.length !== 2) return false;
                    const [lat, lng] = parts.map((val) => parseFloat(val));
                    return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
                })
                .test("coordinates-required-with-address", t("coordinatesRequiredWithAddress"), function (value) {
                    const { address } = this.parent;
                    if (address && address.trim() !== "" && (!value || value.trim() === "")) {
                        return false;
                    }
                    return true;
                }),
            phone: Yup.string(),
            linkUrl: Yup.string(),
            downloadLink: Yup.string(),

            // Localized content validation
            localizedContent: Yup.lazy((obj) => {
                const shape: any = {};
                LANGUAGES.forEach((lang) => {
                    shape[lang.code] = Yup.object({
                        name: Yup.string(),
                        description: Yup.string(),
                        audioUrl: Yup.string(),
                        useProject: Yup.boolean(),
                        selectedProject: Yup.string(),
                        selectedAudioOption: Yup.string(),
                    });
                });
                return Yup.object(shape);
            }),
        })
        .test("at-least-one-name", t("atLeastOneNameRequired"), function (values) {
            const hasAtLeastOneName = LANGUAGES.some((lang) => {
                const content = values.localizedContent?.[lang.code];
                return content?.name && content.name.trim() !== "";
            });

            if (!hasAtLeastOneName) {
                return this.createError({
                    path: `localizedContent.${LANGUAGES[0]?.code}.name`,
                    message: t("atLeastOneNameRequired"),
                });
            }

            return true;
        })
        .test("website-or-download-exclusive", t("websiteOrDownloadLinkRequired"), function (values) {
            // Mutual exclusion: website and download link cannot both be filled
            if (
                values.linkUrl &&
                values.linkUrl.trim() !== "" &&
                values.downloadLink &&
                values.downloadLink.trim() !== ""
            ) {
                return this.createError({
                    path: "downloadLink",
                    message: t("websiteOrDownloadLinkRequired"),
                });
            }
            return true;
        });

    interface SingleTrackFormContentProps {
        projects: any[];
        projectsData: { [key: string]: any };
        setProjectsData: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
        loadingProjects: boolean;
    }

    // Form Component
    const SingleTrackFormContent: React.FC<SingleTrackFormContentProps> = ({
        projects,
        projectsData,
        setProjectsData,
        loadingProjects,
    }) => {
        const { values, setFieldValue, errors, touched } = useFormikContext<UnifiedFormValues>();
        const imageValidation = useImageValidation({
            type: "hubStory",
            imageUrl: values.pictures[0] || undefined,
        });

        // Fetch project details when needed
        const fetchProjectDetails = async (projectId: string) => {
            if (projectsData[projectId]) return projectsData[projectId];

            try {
                const token = localStorage.getItem("token");
                if (!token) return null;

                const response = await getProject({ token, projectId });
                if (response) {
                    setProjectsData((prev) => ({
                        ...prev,
                        [projectId]: response,
                    }));
                    return response;
                }
            } catch (error) {
                console.error("Error fetching project details:", error);
            }
            return null;
        };

        const handlePicturesSelected = useCallback(
            async (files: File | File[]) => {
                const fileList = Array.isArray(files) ? files : [files];
                const token = localStorage.getItem("token");
                if (!token) {
                    console.error("No authentication token found");
                    return;
                }

                await Promise.all(fileList.map((file) => imageValidation.validateImage(file)));

                Promise.all(
                    fileList.map((file) =>
                        uploadFileToS3Complete(
                            {
                                file,
                                isPublic: true, // Public files for general uploads
                                isFolder: false, // No folder structure for general uploads
                            },
                            token,
                        ),
                    ),
                )
                    .then((results) => {
                        setFieldValue("pictures", [...values.pictures, ...results]);
                    })
                    .catch(console.error);
            },
            [setFieldValue, values.pictures, imageValidation.validateImage],
        );

        const setPicturesFromList = useCallback(
            (pictures: string[]) => {
                setFieldValue("pictures", pictures);
            },
            [setFieldValue],
        );

        // Handle audio file upload for specific language
        const handleAudioFileSelected = useCallback(
            (langCode: string) => {
                return (files: File | File[]) => {
                    const file = Array.isArray(files) ? files[0] : files;
                    if (file) {
                        const token = localStorage.getItem("token");
                        if (!token) {
                            console.error("No authentication token found");
                            return;
                        }

                        uploadFileToS3Complete(
                            {
                                file,
                                isPublic: true, // Public files for general uploads
                                isFolder: false, // No folder structure for general uploads
                            },
                            token,
                        )
                            .then((result) => {
                                setFieldValue(`localizedContent.${langCode}.audioUrl`, result);
                            })
                            .catch(console.error);
                    }
                };
            },
            [setFieldValue],
        );

        // Handle project selection for audio
        const handleProjectSelection = useCallback(
            (langCode: string, projectId: string) => {
                setFieldValue(`localizedContent.${langCode}.selectedProject`, projectId);
                setFieldValue(`localizedContent.${langCode}.selectedAudioOption`, "");
                setFieldValue(`localizedContent.${langCode}.audioUrl`, "");
                if (projectId) {
                    fetchProjectDetails(projectId);
                }
            },
            [setFieldValue],
        );

        // Handle audio selection from project
        const handleAudioSelection = useCallback(
            (langCode: string, audioUrl: string) => {
                setFieldValue(`localizedContent.${langCode}.selectedAudioOption`, audioUrl);
                setFieldValue(`localizedContent.${langCode}.audioUrl`, audioUrl);
            },
            [setFieldValue],
        );

        // Toggle project mode for specific language
        const handleProjectToggle = useCallback(
            (langCode: string, useProject: boolean) => {
                setFieldValue(`localizedContent.${langCode}.useProject`, useProject);
                if (!useProject) {
                    setFieldValue(`localizedContent.${langCode}.selectedProject`, "");
                    setFieldValue(`localizedContent.${langCode}.selectedAudioOption`, "");
                }
            },
            [setFieldValue],
        );

        // Remove audio for specific language
        const handleRemoveAudio = useCallback(
            (langCode: string) => {
                setFieldValue(`localizedContent.${langCode}.audioUrl`, "");
                setFieldValue(`localizedContent.${langCode}.selectedProject`, "");
                setFieldValue(`localizedContent.${langCode}.selectedAudioOption`, "");
                setFieldValue(`localizedContent.${langCode}.useProject`, false);
            },
            [setFieldValue],
        );

        // Get audio options for specific language and project
        const getAudioOptionsForLanguage = (langCode: string, projectId: string) => {
            const project = projectsData[projectId];
            if (!project) return [];

            return (
                project.audioAI
                    ?.filter((audio: any) => audio.language === langCode)
                    ?.sort((a: any, b: any) => a.position - b.position)
                    .map((audio: any) => ({
                        value: audio.audioUrl,
                        label: audio.name,
                    })) || []
            );
        };

        // Loading spinner component
        if (isDataLoading) {
            return (
                <div className="form-loading-container">
                    <div className="form-loading-spinner"></div>
                    <p className="form-loading-text">{t("loadingFormData")}</p>
                </div>
            );
        }

        return (
            <Form>
                <div>
                    {/* Position */}
                    <FormGroup
                        title={t("position")}
                        subtitle={t("positionDescription")}
                        className={`${stepStyles.stepDescription} form-group-spacing`}
                    >
                        <FastField name="position" component={NumberField} placeholder={t("enterPosition")} min={0} />
                    </FormGroup>

                    {/* Picture - single image */}
                    <FormGroup title={t("pictures")} className={`${stepStyles.stepDescription} form-group-spacing`}>
                        <ImageSpecsBadges spec="hubStory" warnings={imageValidation.warnings} />
                        <div className={`${stepStyles.fileUpload} form-file-upload-constrained`}>
                            <FileInput
                                onFileSelected={handlePicturesSelected}
                                showButton={true}
                                buttonVariant="outline"
                                multiple={false}
                                acceptedFileTypes={IMAGE_FORMAT}
                                maxSize={IMAGE_MAX_SIZE_200_KB}
                                maxSizeLabel={t("imageMaxSize200kb")}
                            />
                        </div>

                        {values.pictures.length > 0 && (
                            <div className="photo-gallery form-photo-gallery-constrained">
                                <ImageList pictures={values.pictures} setPictures={setPicturesFromList} />
                            </div>
                        )}
                    </FormGroup>

                    {/* Information Block - all optional */}
                    <FormGroup
                        title={t("contactInformation")}
                        subtitle={t("downloadLinkDescription")}
                        className={`${stepStyles.stepDescription} form-group-spacing`}
                    >
                        <FastField
                            name="address"
                            placeholder={t("enterAddress")}
                            component={TextField}
                            size="medium full-width"
                        />

                        {/* Show coordinates field only when address is provided */}
                        {values.address && values.address.trim() !== "" && (
                            <div className="form-coordinates-container" style={{ marginTop: "12px" }}>
                                <Field name="coordinates" placeholder={t("enterCoordinates")} component={TextField} />
                                {touched.coordinates && errors.coordinates && (
                                    <span className="error-message">{errors.coordinates}</span>
                                )}
                            </div>
                        )}

                        <div style={{ marginTop: "12px" }}>
                            <FastField name="phone" placeholder={t("phoneNumber")} component={TextField} />
                        </div>

                        <div style={{ marginTop: "12px" }}>
                            <FastField
                                name="linkUrl"
                                placeholder={t("websiteLinkPlaceholder")}
                                component={TextField}
                                disabled={values.downloadLink && values.downloadLink.trim() !== ""}
                            />
                        </div>

                        <div style={{ marginTop: "12px" }}>
                            <FastField
                                name="downloadLink"
                                placeholder={t("downloadLinkPlaceholder")}
                                component={TextField}
                                disabled={values.linkUrl && values.linkUrl.trim() !== ""}
                            />
                            {touched.downloadLink && errors.downloadLink && (
                                <span className="error-message">{errors.downloadLink}</span>
                            )}
                        </div>
                    </FormGroup>

                    {/* Localized Content */}
                    <FormGroup
                        title={t("localizedVersions")}
                        tooltip={t("localizedVersionsTooltip")}
                        className={`${stepStyles.stepDescription} form-group-spacing`}
                        style={{ marginTop: "40px" }}
                    >
                        <Box
                            sx={{
                                borderBottom: 1,
                                borderColor: "divider",
                                mt: 2,
                                mb: 2,
                            }}
                        >
                            <Tabs
                                value={activeLanguageTab}
                                onChange={(_, newValue) => handleLanguageTabChange(newValue)}
                                sx={{
                                    position: "sticky",
                                    top: 0,
                                    backgroundColor: "white",
                                    zIndex: 10,
                                    borderBottom: "1px solid #e0e0e0",
                                    "& .MuiTabs-flexContainer": {
                                        minHeight: "48px",
                                    },
                                    "& .MuiTab-root": {
                                        minHeight: "48px",
                                        textTransform: "none",
                                        fontWeight: 500,
                                    },
                                }}
                            >
                                {LANGUAGES.map((lang, index) => (
                                    <Tab key={lang.code} label={lang.name} />
                                ))}
                            </Tabs>
                        </Box>

                        {/* Tab Content Container with natural flow */}
                        <Box
                            sx={{
                                pt: 2,
                                pb: 4,
                            }}
                        >
                            {LANGUAGES.map((lang, index) => {
                                const langCode = lang.code;
                                const currentContent = values.localizedContent[langCode] || {
                                    name: "",
                                    description: "",
                                    address: "",
                                    audioUrl: "",
                                    useProject: false,
                                    selectedProject: "",
                                    selectedAudioOption: "",
                                };
                                const projectOptions = projects.map((project) => ({
                                    value: project.id,
                                    label: project.name,
                                }));
                                const audioOptions = currentContent.selectedProject
                                    ? getAudioOptionsForLanguage(langCode, currentContent.selectedProject)
                                    : [];

                                return (
                                    <Box
                                        key={lang.code}
                                        sx={{ display: activeLanguageTab === index ? "block" : "none", pt: 0, pb: 2 }}
                                    >
                                        <FormGroup
                                            title={`${t("name")} (${lang.name})`}
                                            tooltip={t("nameFieldTooltip")}
                                            className={stepStyles.stepDescription}
                                            style={{ marginTop: 0, marginBottom: 20 }}
                                        >
                                            <FastField
                                                name={`localizedContent.${langCode}.name`}
                                                placeholder={t("enterName")}
                                                component={TextField}
                                            />
                                        </FormGroup>

                                        {/* Audio Section */}
                                        <FormGroup
                                            title={`${t("audio")} (${lang.name})`}
                                            className={stepStyles.stepDescription}
                                            style={{ marginTop: 0, marginBottom: 20 }}
                                        >
                                            <div className="editor-section">
                                                <FileInput
                                                    onFileSelected={handleAudioFileSelected(langCode)}
                                                    placeholder={t("dragAndDropYourAudioFileHereOrClickToSelectOne")}
                                                    acceptedFileTypes={audioAccept}
                                                />

                                                <div className="radio-text-bold" style={{ marginTop: "20px" }}>
                                                    <RadioButton
                                                        label={t("addFromYourProjects")}
                                                        checked={currentContent.useProject}
                                                        onChange={() =>
                                                            handleProjectToggle(langCode, !currentContent.useProject)
                                                        }
                                                        activeImage={activeRadio}
                                                        inactiveImage={inactiveRadio}
                                                        center={true}
                                                    />
                                                </div>

                                                {currentContent.useProject && (
                                                    <div
                                                        className="dropdown-row"
                                                        style={{ marginTop: "10px", marginLeft: 0 }}
                                                    >
                                                        <Dropdown
                                                            label=""
                                                            options={projectOptions}
                                                            onChange={(value) =>
                                                                handleProjectSelection(langCode, value)
                                                            }
                                                            placeholder={t("selectProject")}
                                                            loading={false}
                                                            error={false}
                                                            value={currentContent.selectedProject}
                                                        />
                                                        {currentContent.selectedProject && (
                                                            <Dropdown
                                                                label=""
                                                                options={audioOptions}
                                                                onChange={(value) =>
                                                                    handleAudioSelection(langCode, value)
                                                                }
                                                                loading={false}
                                                                error={false}
                                                                placeholder={t("selectAnAudio")}
                                                                value={currentContent.selectedAudioOption}
                                                            />
                                                        )}
                                                    </div>
                                                )}

                                                {currentContent.audioUrl && (
                                                    <div className={audioForm.container}>
                                                        <div className={audioForm.header}>
                                                            <span className={audioForm.title}>
                                                                {t("audioTrack")} ({lang.name})
                                                            </span>
                                                            <Button
                                                                label={t("remove")}
                                                                onClick={() => handleRemoveAudio(langCode)}
                                                                variant="danger"
                                                                isSmall
                                                                preventDefault
                                                                className={audioForm.removeBtn}
                                                            />
                                                        </div>
                                                        <HorizontalSeparator />
                                                        <div className={audioForm.content}>
                                                            <AudioPlayerRewind
                                                                title=""
                                                                src={currentContent.audioUrl}
                                                                isAutoPlay={false}
                                                                hideDownload={true}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </FormGroup>

                                        <FormGroup
                                            title={`${t("description")} (${lang.name})`}
                                            className={`${stepStyles.stepDescription} form-group-spacing`}
                                            style={{ marginTop: 0, marginBottom: 20 }}
                                        >
                                            <FastField
                                                name={`localizedContent.${langCode}.description`}
                                                placeholder={t("enterDescription")}
                                                component={TextField}
                                                size="big-with-border"
                                            />
                                        </FormGroup>
                                    </Box>
                                );
                            })}
                        </Box>
                    </FormGroup>
                </div>
            </Form>
        );
    };

    // Initial form values
    const getInitialValues = (): UnifiedFormValues => {
        const localizedContent: { [key: string]: LocalizedContent } = {};

        LANGUAGES.forEach((lang) => {
            localizedContent[lang.code] = {
                name: "",
                description: "",
                audioUrl: "",
                useProject: false,
                selectedProject: "",
                selectedAudioOption: "",
            };
        });

        return {
            localizedContent,
            address: "",
            coordinates: "",
            phone: "",
            linkUrl: "",
            downloadLink: "",
            pictures: [],
            position: 0,
            type: "story", // Default to story
        };
    };

    // Navigation function to go back to content list
    const handleGoToContentList = () => {
        navigate(`/hubs/folders/categories/tracks?hubId=${hubId}&folderId=${folderId}&categoryId=${categoryId}`);
    };

    // Handle form submission
    const handleSubmit = async (values: UnifiedFormValues, formikHelpers: FormikHelpers<UnifiedFormValues>) => {
        const { resetForm } = formikHelpers;
        const token = localStorage.getItem("token");
        if (!token || !hubId || !categoryId) {
            console.error("Missing token");
            return;
        }

        try {
            setModalOpen(true);
            setStatus("waiting");

            const payload = {
                localizedData: Object.entries(values.localizedContent).map(([langCode, content]) => ({
                    name: content.name,
                    description: content.description || undefined,
                    language: langCode,
                    audioUrl: content.audioUrl || undefined,
                    address: values.address || undefined,
                })),
                coverImage: values.pictures?.[0],
                pictures: values.pictures.map((url, index) => ({
                    url,
                    position: index,
                })),
                type: "story",
                folderId: hubId,
                categoryId: categoryId,
                companySingleTrackFolderId: folderId,
                position: values.position,
                // Information block
                phone: values.phone || undefined,
                linkUrl: values.linkUrl || undefined,
                downloadLink: values.downloadLink || undefined,
                point:
                    values.coordinates && values.coordinates.trim() !== ""
                        ? {
                              latitude: parseFloat(values.coordinates.split(",")[0]),
                              longitude: parseFloat(values.coordinates.split(",")[1]),
                          }
                        : undefined,
            };

            await createSingleTrackStory({ token, data: payload });

            setStatus("success");
            setTimeout(() => resetForm(), 100);

            // Note: Removed automatic redirect - user will choose via modal buttons
        } catch (err) {
            console.error("Error creating single track:", err);
            setStatus("error");
        }
    };

    const flattenErrors = (errors: any): string[] => {
        const result: string[] = [];
        Object.values(errors).forEach((value) => {
            if (typeof value === "string") {
                result.push(value);
            } else if (typeof value === "object" && value !== null) {
                result.push(...flattenErrors(value));
            }
        });
        return result;
    };

    return (
        <div className="single-track-form-page-container">
            <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                key={i18n.language}
                validateOnMount
                enableReinitialize={true}
                validateOnChange={true}
                validateOnBlur={true}
            >
                {({ isSubmitting, submitForm, errors }) => {
                    return (
                        <>
                            <SingleTrackFormContent
                                projects={projects}
                                projectsData={projectsData}
                                setProjectsData={setProjectsData}
                                loadingProjects={loadingProjects}
                            />

                            <div className="single-track-form-actions">
                                <Tooltip
                                    title={
                                        Object.keys(errors).length > 0
                                            ? t("formIncompleteTooltip", {
                                                  fields: flattenErrors(errors).join(", "),
                                              })
                                            : ""
                                    }
                                    arrow
                                >
                                    <span>
                                        <Button
                                            variant="primary"
                                            label={t("createSingleTrack")}
                                            onClick={submitForm}
                                            disabled={isSubmitting || Object.keys(errors).length > 0}
                                        />
                                    </span>
                                </Tooltip>
                            </div>

                            {/* Modal after creation */}
                            <UnifiedSingleTrackCreationModal
                                isOpen={modalOpen}
                                status={status}
                                onClose={() => setModalOpen(false)}
                                onCreateAnother={() => setModalOpen(false)}
                                onGoToContentList={() => {
                                    setModalOpen(false);
                                    handleGoToContentList();
                                }}
                            />
                        </>
                    );
                }}
            </Formik>
        </div>
    );
};

export default UnifiedSingleTrackForm;
